import AbstractModel from './utils/abstractModel'
import types from './utils/types'
import env from '@shared/env.js'
import { decodeAmpersands } from '@shared/utils/decodeAmpersands'

// use the same keys here to match the incentive types coming from the API
export const INCENTIVE_TYPES = {
  exercise_mat: 'exercise-mat',
  //habit-cards: 'habit-cards',
  fitbit: 'fitbit',
}

export const CHANNELS = {
  direct: 'direct',
  virgin_pulse: 'virgin_pulse',
  surest: 'surest',
  solera: 'solera',
  fully_insured: 'fully_insured',
  de_insurers: 'de_insurers',
  providence: 'providence',
  uhcmedsupp: 'uhcmedsupp',
  westernhealth: 'westernhealth',
  umr: 'umr',
  uhg: 'uhg',
  optumcare: 'optumcare',
  uhcvpt: 'uhcvpt',
  converge: 'converge',
  optum: 'optum',
  uhcnational: 'uhcnational',
  demo: 'demo',
  referralprograms: 'referralprograms',
}

export const TERMINATION_REASONS = {
  sword_migration: 'sword_migration',
  other: 'other',
}

export const isTerminatedForSwordMigration = (corporate) =>
  (corporate?.isCustomer ?? corporate?.is_customer) === false &&
  (corporate?.terminationReason || corporate?.termination_reason) ===
    TERMINATION_REASONS.sword_migration

const getExternalPortalUrl = (corporate) => {
  const key = corporate.key.toLowerCase()
  const channel = corporate.corporateChannel?.toLowerCase()
  switch (true) {
    case ['webmd-one', 'wellwisconsin'].includes(key):
      if (!env.isProd) {
        return 'https://staging.webmdhealth.com/wellwisconsin/default.aspx?spid=764'
      }
      return 'https://webmdhealth.com/wellwisconsin/default.aspx?spid=764'
    case [CHANNELS.fully_insured, CHANNELS.optum].includes(channel):
      return 'https://www.myuhc.com/'
    default:
      return undefined
  }
}

const getExternalPortalName = (corporate) => {
  const key = corporate.key.toLowerCase()
  const channel = corporate.corporateChannel?.toLowerCase()
  switch (true) {
    case ['webmd-one', 'wellwisconsin'].includes(key):
      return 'WebMD One'
    case [CHANNELS.fully_insured, CHANNELS.optum].includes(channel):
      return 'UHC Hub'
    default:
      return undefined
  }
}

class Corporate extends AbstractModel {
  // This model is intended to be used whenever we're dealing with a corporate object rendered by the API

  /**
   * @type {array.<string>}
   * @readonly
   */
  corporateTypes

  /**
   * @type {string}
   * @readonly
   */
  corporateChannel

  /**
   * not coming from API. Can be used in order to customize the title of the corporate in some flow (e.g. dropdown...)
   * @param {string | undefined}
   */
  customTitle

  /**
   * @type {number}
   * @readonly
   */
  id

  /**
   * @type {boolean}
   * @readonly
   */
  isCustomer

  /**
   * @type {boolean}
   * @readonly
   */
  isPrivateInsurance

  /**
   * @type {boolean}
   * @readonly
   */
  isPublicInsurance

  /**
   * @type {string}
   * @readonly
   */
  key

  /**
   * @param {string}
   * @readonly
   */
  logoFile

  /**
   * @param {string}
   * @readonly
   */
  preferredSignupMethod

  /**
   * @param {string}
   * @readonly
   */
  prescriptionUploadMode

  /**
   * @type {object, null}
   * @readonly
   */
  primaryAddress

  /**
   * @param {string}
   * @readonly
   */
  signupInfoPageUrl

  /**
   * @type {string}
   * @readonly
   */
  title

  /**
   * @param {Object.<string, string>}
   * @readonly
   */
  translations

  /**
   * @param {string}
   * @readonly
   */
  personifyHealthSponsorId

  /**
   * @type {string | null}
   * @readonly
   */
  terminationReason

  /**
   * @type {number | null}
   * @readonly
   */
  corporateEligibilityGroupId

  /**
   * @param {string | undefined}
   */
  externalPortalUrl

  /**
   * @param {string | undefined}
   */
  externalPortalName

  constructor(json) {
    super(json)
    this.id = json.id
    this.title = decodeAmpersands(json.title)
    this.customTitle = json.custom_title || this.title
    this.isCustomer = json.is_customer || false
    this.key = json.key
    this.isPublicInsurance = json.is_public_insurance || false
    this.isPrivateInsurance = json.is_private_insurance || false
    this.corporateTypes = json.corporate_types || []
    this.corporateChannel = json.corporate_channel
    this.preferredSignupMethod = json.preferred_signup_method
    this.logoFile = json.logo_file
    this.primaryAddress = json.primary_address || {}
    this.translations = json.translations
    this.signupInfoPageUrl = json.signup_info_page_url
    this.prescriptionUploadMode = json.prescription_upload_mode
    this.personifyHealthSponsorId = json.virgin_pulse_sponsor_id

    this.externalPortalUrl = getExternalPortalUrl(this)
    this.externalPortalName = getExternalPortalName(this)
    this.terminationReason = json.termination_reason || null
    this.corporateEligibilityGroupId =
      json.corporate_eligibility_group_id || null

    this.validate()
  }

  validate() {
    this.assertType('id', [types.NUMBER])
    this.assertType('title', [types.STRING])
    this.assertType('customTitle', [types.STRING, types.NULL])
    this.assertType('isCustomer', [types.BOOLEAN])
    this.assertType('key', [types.STRING])
    this.assertType('isPublicInsurance', [types.BOOLEAN])
    this.assertType('isPrivateInsurance', [types.BOOLEAN])
    this.assertType('corporateTypes', [types.ARRAY])
    this.assertType('corporateChannel', [types.STRING, types.NULL])
    this.assertType('preferredSignupMethod', [types.STRING, types.NULL])
    this.assertType('logoFile', [types.STRING, types.NULL])
    this.assertType('translations', [types.JSON, types.NULL])
    this.assertType('signupInfoPageUrl', [types.STRING, types.NULL])
    this.assertType('prescriptionUploadMode', [types.STRING, types.NULL])
    this.assertType('primaryAddress', [types.JSON])
    this.assertType('personifyHealthSponsorId', [types.STRING, types.NULL])
    this.assertType('externalPortalUrl', [types.STRING, types.NULL])
    this.assertType('externalPortalName', [types.STRING, types.NULL])
    this.assertType('terminationReason', [types.STRING, types.NULL])
    this.assertType('corporateEligibilityGroupId', [types.NUMBER, types.NULL])
  }

  /**
   * Check if the corporate is a demo corporate
   * @returns {boolean}
   * @readonly
   */
  get isDemo() {
    return !this.isPublicInsurance && !this.isPrivateInsurance
  }

  /**
   * Type of insurance of the corporate
   * @returns {'private' | 'public' | 'not_set'}
   * @readonly
   */
  get insuranceType() {
    if (this.isPrivateInsurance && !this.isPublicInsurance) {
      return 'private'
    } else if (!this.isPrivateInsurance && this.isPublicInsurance) {
      return 'public'
    } else {
      return 'not_set'
    }
  }

  /**
   * Check if the corporate is a private insurance with a contract - de only
   * @returns {boolean}
   * @readonly
   */
  get isPrivateWithContract() {
    return isPrivateWithContract(this.key)
  }
}

export const isPrivateWithContract = (key) =>
  ['dkv', 'carelutions', 'huk', 'generalihealthsolutions'].includes(key)

export default Corporate
